<script setup lang="ts">
import type { IProps } from './types'
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage
} from '~/components/ui/breadcrumb'

const props = defineProps<IProps>()
const route = useRoute()
const { t } = useI18n()

// Try to use existing SEO data from app.vue first, fallback to cache system
const nuxtApp = useNuxtApp()
const existingSeoData = computed(() => nuxtApp._seoData?.value)

// Only load cache data if no existing SEO data is available
const { data: cachedData } = existingSeoData.value
  ? { data: ref({ data: { data: {} } }) }
  : await useAppCache()

const replaceVariables = (template: string, vars?: Record<string, string>): string =>
  !vars ? template : template.replace(/:([a-zA-Z0-9_]+):/g, (_, key) => vars[key] ?? `:${key}`)

const seoData = computed(() => {
  // Use existing SEO data if available
  if (existingSeoData.value) {
    return existingSeoData.value
  }

  // Fallback to cache system
  let currentPath = route.path.startsWith('/') ? route.path.slice(1) : route.path

  if (currentPath.startsWith('ky/')) {
    currentPath = currentPath.substring(3)
  }

  const seoEntries = cachedData.value?.data?.data || {}

  if (seoEntries[currentPath]) return seoEntries[currentPath]

  for (const key in seoEntries) {
    if (key.includes('(')) {
      const regex = new RegExp(`^${key}$`)
      if (regex.test(currentPath)) {
        return seoEntries[key]
      }
    }
  }

  return null
})

const computedLinks = computed(() => {
  const links = [...props.links]
  if (links.length && seoData.value?.breadcrumb_title) {
    links[links.length - 1] = {
      ...links[links.length - 1],
      title: replaceVariables(seoData.value.breadcrumb_title, props.variables)
    }
  }

  return links
})

const breadcrumbItems = computed(() => [
  {
    '@type': 'ListItem',
    position: 1,
    name: t('common.breadcrumbs.home'),
    item: '/'
  },
  ...computedLinks.value.map((link, index) => ({
    '@type': 'ListItem',
    position: index + 2,
    name: link.title,
    ...(link.url && { item: link.url })
  }))
])

useSchemaOrg([
  defineBreadcrumb({
    itemListElement: computed(() => breadcrumbItems.value)
  })
])
</script>

<template>
  <Breadcrumb>
    <BreadcrumbList>
      <BreadcrumbItem>
        <BreadcrumbLink as-child>
          <NuxtLinkLocale to="/">{{ $t('common.breadcrumbs.home') }}</NuxtLinkLocale>
        </BreadcrumbLink>
      </BreadcrumbItem>
      <template v-for="(link, index) in computedLinks" :key="index">
        <BreadcrumbSeparator>/</BreadcrumbSeparator>
        <BreadcrumbItem v-if="computedLinks.length > index + 1 && link.url">
          <BreadcrumbLink as-child>
            <NuxtLinkLocale :to="link.url">{{ link.title }}</NuxtLinkLocale>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem v-else>
          <BreadcrumbPage>{{ link.title }}</BreadcrumbPage>
        </BreadcrumbItem>
      </template>
    </BreadcrumbList>
  </Breadcrumb>
</template>
